'use client';

import type { Member } from '@slims/shared/types';
import * as React from 'react';
import { useState } from 'react';
import { Download, FileText, X } from 'lucide-react';
import { toast } from 'sonner';

import { Row, Stack, Text } from '../../atoms';
import {
	BodySkeleton,
	CardBadge,
	ConfirmationDialog,
	DashGrid,
	Dialog,
	DialogContent,
	DialogTitle,
	PanelCard,
	ReportCardRow,
	SegmentedTabs,
	TONE_VAR,
	type WidgetTone,
} from '../../molecules';
import { DetailField, DetailTextInput } from '../../molecules/detail-fields/detail-fields';
import { LabeledSelect } from '../../molecules/labeled-select/labeled-select';

/**
 * Generatore di report — allineato pixel per pixel alla sezione "Report"
 * (`#s-report`) di `docs/latest/SLIMS-CER_v11.html`: ambito/periodo/blocchi di
 * contenuto/formato con anteprima live, report generati. Sostituisce
 * l'adattamento precedente da `SLIMS-CER_v6.html` (`docs/mocks/report-builder-mock.md`)
 * — v11 rimuove l'ambito "GSE (adempimenti)", il formato XML e i "Modelli rapidi";
 * il pannello degli adempimenti è stato tolto con la parte burocratica.
 */

export type ReportScope = 'comunita' | 'socio' | 'impianto';
export type ReportFormat = 'pdf' | 'excel';
export type ReportBlockCategory = 'ENERGIA' | 'INCENTIVI' | 'AI' | 'CONFORMITA';

export interface ReportBlockDef {
	id: string;
	cat: ReportBlockCategory;
	title: string;
	sub: string;
}

/**
 * Testo al posto del valore di un blocco. Nessun blocco è ancora collegato ai
 * dati della configurazione (INB-358): prima ogni blocco aveva un valore di
 * esempio scritto a mano, e il PDF generato lo stampava come se fosse vero.
 */
export const REPORT_VALUE_UNAVAILABLE = 'dato non ancora disponibile';

export interface GeneratedReportItem {
	id: string;
	title: string;
	sub: string;
	tone: 'green' | 'blue' | 'purple' | 'orange';
	status: string;
}

/** Una sezione del report generato: una categoria di blocchi con le sue righe. */
export interface ReportSpecSection {
	cat: ReportBlockCategory;
	label: string;
	/**
	 * `value` è il valore del blocco nel periodo; `null` finché il blocco non è
	 * collegato ai dati. I report salvati prima di INB-358 hanno `sample` al suo
	 * posto e nessun `value`: vanno trattati come `null`, mai stampati.
	 */
	blocks: { id: string; title: string; sub: string; value?: string | null }[];
}

/**
 * Descrizione pura di un report da produrre: la vista la compone dalle scelte
 * dell'utente e la passa a `onGenerateReport`. Il cablaggio dell'app ne ricava
 * il file (PDF/Excel), lo apre e lo conserva — nessun blob, finestra o storage
 * dentro `@slims/ui`.
 */
export interface ReportSpec {
	scope: ReportScope;
	format: ReportFormat;
	/** Titolo del documento (es. «Report Comunità», «Report Socio - Mario Rossi»). */
	title: string;
	/** Riga di dettaglio sotto il titolo: soggetto · periodo · etichetta periodo. */
	meta: string;
	/** Soggetto del report: nome del socio, oppure il nome della configurazione. */
	subject: string;
	period: string;
	periodLabel: string;
	flags: { logo: boolean; firma: boolean; grafici: boolean };
	sections: ReportSpecSection[];
	/** Socio del report, per l'ambito «Singolo socio»: serve a leggerne i valori (INB-380). */
	memberId?: string | null;
	/** Impianto del report, per l'ambito «Impianto» (INB-395). */
	plantId?: string | null;
	/** Periodo «Personalizzato»: primo e ultimo giorno, `AAAA-MM-GG`, inclusi (INB-395). */
	customFrom?: string | null;
	customTo?: string | null;
	/**
	 * Logo della CER per l'intestazione, già caricato dal cablaggio (INB-380).
	 * Assente: il PDF scrive «SLIMS» come prima.
	 */
	logo?: { dataUrl: string; width: number; height: number } | null;
	/** Numero di pagine stimato mostrato in anteprima. */
	pages: number;
	/** Momento della generazione, ISO. */
	generatedAt: string;
}

/**
 * Vista attiva della pagina Report quando più configurazioni sono selezionate:
 * `multi` è la griglia «Panoramica» (una card per configurazione), `single` è
 * il generatore completo di una configurazione sola. Stesso schema di
 * Dashboard, Monitoraggio, Previsione, Incentivi e Anomalie.
 */
export type ReportBuilderViewMode = 'multi' | 'single';

/** Voce del picker di configurazione, in modalità singola. */
export interface ReportBuilderConfigPickerItem {
	id: string;
	cerNome: string;
	configNome: string;
}

/** Un impianto della configurazione, per il selettore dell'ambito «Impianto». */
export interface ReportPlantOption {
	id: string;
	/** Nome leggibile: impianto, potenza e socio. */
	label: string;
}

export interface ReportBuilderViewProps {
	/** Nome della CER, usato nell'intestazione dell'anteprima per l'ambito "Comunità". */
	cerNome: string;
	/** Soci della CER, per il selettore "Singolo socio" — già disponibili sulla pagina. */
	members: Member[];
	/** Impianti della configurazione, per il selettore "Impianto" (INB-395). */
	plants?: ReportPlantOption[];
	/**
	 * `false` per una CER con i suggerimenti AI spenti: i blocchi sulle
	 * raccomandazioni spariscono, e la categoria resta «Richieste». Default `true`.
	 */
	aiEnabled?: boolean;
	/** Elenco iniziale dei report già generati (storico). */
	initialGenerated: GeneratedReportItem[];
	/**
	 * Chiamato alla generazione di un report. Se presente, la vista non tiene un
	 * elenco interno: passa la {@link ReportSpec} al cablaggio, che costruisce il
	 * file, lo apre e lo persiste, e restituisce l'elenco aggiornato via
	 * `generatedReports`. Se assente, la vista aggiunge una riga fittizia in
	 * memoria a `initialGenerated` (storie/demo).
	 */
	onGenerateReport?: (spec: ReportSpec) => void;
	/**
	 * Elenco controllato dei report generati (dal cablaggio, tipicamente da
	 * `localStorage`). Se presente ha la precedenza su `initialGenerated`.
	 */
	generatedReports?: GeneratedReportItem[];
	/** Riapre un report già generato (in anteprima, se il cablaggio passa `pdfPreview`). */
	onOpenReport?: (id: string) => void;
	/**
	 * PDF da mostrare in una finestra dell'app (INB-381), come nell'archivio
	 * documenti: il cablaggio crea l'URL del file e lo revoca alla chiusura.
	 * `null` o assente = nessuna finestra aperta.
	 */
	pdfPreview?: { url: string; title: string } | null;
	/** Chiude la finestra di anteprima. */
	onClosePdfPreview?: () => void;
	/** Scarica il PDF mostrato in anteprima. */
	onDownloadPdfPreview?: () => void;
	/** Scarica un report già generato. */
	onDownloadReport?: (id: string) => void;
	/** Elimina un report già generato dallo storico. */
	onDeleteReport?: (id: string) => void;
	/**
	 * Fetch dei soci in corso. Il resto della card «Composizione report»
	 * (ambito, periodo, blocchi, formato, opzioni) è tutto stato locale e non
	 * aspetta nessun dato: solo il selettore «Singolo socio» mostra uno
	 * skeleton, così non lampeggia «Nessun socio disponibile» prima del fetch.
	 */
	membersLoading?: boolean;
	// ── Panoramica configurazioni
	/**
	 * Vista attiva. Assente (o `configs` con un solo elemento): niente toggle,
	 * si mostra solo il generatore — il comportamento di prima della «Panoramica».
	 */
	view?: ReportBuilderViewMode;
	onViewChange?: (view: ReportBuilderViewMode) => void;
	/** Configurazioni disponibili per il picker di modalità singola. */
	configs?: ReportBuilderConfigPickerItem[];
	/** Configurazione scelta nel picker, in modalità singola. */
	selectedConfigId?: string | null;
	onSelectConfig?: (id: string) => void;
	/**
	 * Contenuto della griglia «Panoramica»: una {@link ReportBuilderConfigCard}
	 * per configurazione, fornita dal wiring — ognuna fa il proprio fetch, la
	 * vista non sa come si ottiene.
	 */
	overviewGridSlot?: React.ReactNode;
}

// ─── Panoramica configurazioni ───────────────────────────────────────────────

const REPORT_VIEW_TABS: { value: ReportBuilderViewMode; label: string }[] = [
	{ value: 'multi', label: 'Panoramica' },
	{ value: 'single', label: 'Configurazione singola' },
];

/** Dati di una card della «Panoramica» — la composizione soci di una configurazione sola. */
export interface ReportBuilderConfigCardData {
	id: string;
	cerNome: string;
	configNome: string;
	comune?: string | null;
	areaCode?: string | null;
	/** Soci non receduti. */
	sociAttivi: number;
	/** Soci con ruolo produttore o prosumer. */
	produttori: number;
	/** Soci con ruolo consumatore. */
	consumatori: number;
	/** Soci ancora in attesa di approvazione. */
	inAttesa: number;
}

export interface ReportBuilderConfigCardProps {
	data: ReportBuilderConfigCardData;
	onClick?: () => void;
}

function ConfigMetricCell({
	label,
	value,
	tone,
}: {
	label: string;
	value: string;
	tone: WidgetTone;
}) {
	return (
		<div className="rounded-[7px] border border-border bg-background px-[9px] py-[7px]">
			<div className="mb-0.5 text-[9px] font-extrabold uppercase tracking-wide text-text-muted">
				{label}
			</div>
			<div className="font-mono text-sm font-extrabold" style={{ color: TONE_VAR[tone] }}>
				{value}
			</div>
		</div>
	);
}

/** Card compatta di una configurazione nella «Panoramica» dei Report: la composizione soci, cliccabile per aprirne il generatore. */
export function ReportBuilderConfigCard({ data, onClick }: ReportBuilderConfigCardProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="w-full cursor-pointer rounded-[11px] border border-border bg-card px-3.5 py-[13px] text-left transition-[border-color,transform] duration-150 hover:-translate-y-0.5 hover:border-accent-purple"
		>
			<div className="mb-3 flex items-start justify-between gap-2">
				<div className="min-w-0">
					<div className="mb-1 truncate text-[9.5px] font-extrabold uppercase tracking-wide text-accent">
						{data.cerNome}
					</div>
					<div className="truncate text-sm font-extrabold leading-tight">{data.configNome}</div>
					<div className="mt-[3px] truncate text-[11px] text-text-muted">
						{data.comune}
						{data.areaCode && (
							<>
								{' · '}
								<span className="font-mono text-accent">{data.areaCode}</span>
							</>
						)}
					</div>
				</div>
				<span className="flex-none font-mono text-[9px] font-extrabold text-accent-green">
					{data.sociAttivi} soci
				</span>
			</div>
			<div className="mb-3 grid grid-cols-2 gap-2">
				<ConfigMetricCell label="Soci attivi" value={String(data.sociAttivi)} tone="success" />
				<ConfigMetricCell label="In attesa" value={String(data.inAttesa)} tone="warn" />
				<ConfigMetricCell label="Produttori" value={String(data.produttori)} tone="purple" />
				<ConfigMetricCell label="Consumatori" value={String(data.consumatori)} tone="accent" />
			</div>
			<Text as="span" variant="caption" weight="bold" tone="accent">
				clic → report
			</Text>
		</button>
	);
}

export interface ReportBuilderConfigCardSkeletonProps {
	/** Nome della CER — l'unico dato già noto (arriva dallo scope-tree, non dal fetch di questa card): resta nitido, il resto sfoca. */
	cerNome: string;
}

/** Guscio di {@link ReportBuilderConfigCard} in caricamento — stessa cornice/padding della card reale. */
export function ReportBuilderConfigCardSkeleton({ cerNome }: ReportBuilderConfigCardSkeletonProps) {
	return (
		<div
			className="w-full rounded-[11px] border border-border bg-card px-3.5 py-[13px] text-left"
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- guscio di card generico (`div`), stesso pattern di `PanelCardSkeleton`.
			role="status"
			aria-label={`Caricamento configurazione - ${cerNome}`}
		>
			<div className="mb-3 flex items-start justify-between gap-2">
				<div className="min-w-0 flex-1">
					<div className="mb-1 truncate text-[9.5px] font-extrabold uppercase tracking-wide text-accent">
						{cerNome}
					</div>
					<BodySkeleton className="mb-1.5 h-4 w-32" />
					<BodySkeleton className="h-3 w-40" />
				</div>
				<BodySkeleton className="h-4 w-12 flex-none rounded-full" />
			</div>
			<div className="mb-3 grid grid-cols-2 gap-2">
				{Array.from({ length: 4 }).map((_, i) => (
					<div
						key={i}
						className="rounded-[7px] border border-border bg-background px-[9px] py-[7px]"
					>
						<BodySkeleton className="mb-1 h-2.5 w-20" />
						<BodySkeleton className="h-4 w-16" />
					</div>
				))}
			</div>
			<BodySkeleton className="h-3 w-28" />
		</div>
	);
}

const CAT_META: Record<ReportBlockCategory, { label: string }> = {
	ENERGIA: { label: 'Energia & Impianti' },
	INCENTIVI: { label: 'Energia condivisa' },
	AI: { label: 'Raccomandazioni AI' },
	// Il mock usa la chiave 'CONFORMITÀ' (con accento) — qui 'CONFORMITA' per restare
	// un identificatore JS semplice.
	CONFORMITA: { label: 'Dati GSE' },
};

/**
 * I blocchi selezionabili. Partono da `BLOCKS` del mock v11, senza quelli che
 * il mock non mostrava (`i5` CO₂, `a3` incentivi AI aggiuntivi, `g2` XML GSE)
 * e senza quelli contabili (`g4` rendicontazione benefici, `g5` registro dei
 * flussi, `i4` risparmio in bolletta), tolti con la parte finanziaria, né le
 * richieste di variazione (`a4`, `a5`), tolte con la parte burocratica.
 */
const BLOCKS: ReportBlockDef[] = [
	{
		id: 'e1',
		cat: 'ENERGIA',
		title: 'Produzione FV',
		sub: 'giornaliera · mensile · storico',
	},
	{
		id: 'e2',
		cat: 'ENERGIA',
		title: 'Consumi per fascia F1/F2/F3',
		sub: 'da dati GSE',
	},
	{
		id: 'e3',
		cat: 'ENERGIA',
		title: 'Autoconsumo e quota ceduta',
		sub: 'diretto + condiviso al portfolio',
	},
	{
		id: 'e4',
		cat: 'ENERGIA',
		title: 'Stato e rendimento impianto',
		sub: 'anomalie · performance',
	},
	{
		id: 'i1',
		cat: 'INCENTIVI',
		title: 'kWh condivisi e quota %',
		sub: 'sul totale nel mese',
	},
	{
		id: 'a1',
		cat: 'AI',
		title: 'Raccomandazioni AI ricevute',
		sub: 'carichi da spostare nelle ore di sole',
	},
	{
		id: 'a2',
		cat: 'AI',
		title: 'Raccomandazioni seguite',
		sub: 'n° assoluto e % sul totale',
	},
	{
		id: 'g3',
		cat: 'CONFORMITA',
		title: 'Riconciliazione energetica',
		sub: 'misuratori M1 / M2',
	},
];

/** I blocchi che parlano di raccomandazioni AI, nascosti a funzionalità spenta. */
const AI_BLOCK_IDS = new Set(['a1', 'a2']);

const PRESETS: Record<ReportScope, string[]> = {
	comunita: ['i1', 'e3', 'g3'],
	socio: ['e1', 'e2', 'e3', 'i1', 'a1', 'a2'],
	impianto: ['e1', 'e4', 'e3', 'i1'],
};

const SCOPE_OPTIONS: { value: ReportScope; label: string }[] = [
	{ value: 'comunita', label: 'Comunità' },
	{ value: 'socio', label: 'Singolo socio' },
	{ value: 'impianto', label: 'Impianto' },
];

const SCOPE_TITLE: Record<ReportScope, string> = {
	comunita: 'Report Comunità',
	socio: 'Report Socio',
	impianto: 'Report Impianto',
};

const PERIOD_NAME: Record<string, string> = {
	mese: 'Mese',
	trimestre: 'Trimestre',
	anno: 'Anno',
	custom: 'Personalizzato',
};

const PERIOD_OPTIONS = [
	{ value: 'mese', label: 'Mese' },
	{ value: 'trimestre', label: 'Trimestre' },
	{ value: 'anno', label: 'Anno' },
	{ value: 'custom', label: 'Personalizzato' },
];

const FORMAT_OPTIONS: { value: ReportFormat; label: string }[] = [
	{ value: 'pdf', label: 'PDF' },
	{ value: 'excel', label: 'Excel' },
];

function memberLabel(m: Member): string {
	return m.nome || m.email || 'Socio';
}

/**
 * Etichetta del periodo per la data corrente — il mock usa valori fissi ("Giugno
 * 2026", "Q2 2026"): qui calcolati dalla data reale, stesso formato.
 */
function isoDay(d: Date): string {
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function firstOfMonth(d: Date): Date {
	return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** `AAAA-MM-GG` → `GG/MM/AAAA`. */
function itDay(iso: string): string {
	const [y, m, d] = iso.split('-');
	return `${d}/${m}/${y}`;
}

/**
 * Etichetta del periodo. Mese, trimestre e anno sono quelli in corso, e il
 * report li conta fino a oggi (INB-395): l'etichetta lo dice, così un report
 * del 6 ottobre non si legge come il mese intero.
 */
function periodLabel(period: string, now: Date, custom?: { from: string; to: string }): string {
	const finoA = ` · fino al ${itDay(isoDay(now))}`;
	switch (period) {
		case 'mese':
			return `${now.toLocaleString('it-IT', { month: 'long', year: 'numeric' })}${finoA}`;
		case 'trimestre':
			return `Q${Math.ceil((now.getMonth() + 1) / 3)} ${now.getFullYear()}${finoA}`;
		case 'anno':
			return `${now.getFullYear()}${finoA}`;
		case 'custom':
			return custom?.from && custom.to
				? `dal ${itDay(custom.from)} al ${itDay(custom.to)}`
				: 'Periodo personalizzato';
		default:
			return '';
	}
}

/** Pillola di scelta singola (`.rb-opt`/`.rb-seg` del mock). */
function PillRow<T extends string>({
	options,
	value,
	onChange,
}: {
	options: { value: T; label: string }[];
	value: T;
	onChange: (v: T) => void;
}) {
	return (
		<div className="rb-seg">
			{options.map((o) => (
				<button
					key={o.value}
					type="button"
					className={o.value === value ? 'rb-opt on' : 'rb-opt'}
					onClick={() => onChange(o.value)}
				>
					{o.label}
				</button>
			))}
		</div>
	);
}

/** Pillole a scelta multipla (stesse classi `.rb-opt`/`.rb-seg` di {@link PillRow}, toggle indipendenti). */
function FlagPillRow({
	options,
	flags,
	onToggle,
}: {
	options: { key: 'logo' | 'firma' | 'grafici'; label: string }[];
	flags: Record<'logo' | 'firma' | 'grafici', boolean>;
	onToggle: (key: 'logo' | 'firma' | 'grafici') => void;
}) {
	return (
		<div className="rb-seg">
			{options.map((o) => (
				<button
					key={o.key}
					type="button"
					className={flags[o.key] ? 'rb-opt on' : 'rb-opt'}
					onClick={() => onToggle(o.key)}
				>
					{o.label}
				</button>
			))}
		</div>
	);
}

function BlockCheckbox({
	block,
	checked,
	onToggle,
}: {
	block: ReportBlockDef;
	checked: boolean;
	onToggle: () => void;
}) {
	return (
		<button type="button" className={checked ? 'rb-block on' : 'rb-block'} onClick={onToggle}>
			<div className="rb-chk">{checked ? '✓' : ''}</div>
			<div>
				<div className="rb-btxt">{block.title}</div>
				<div className="rb-bsub">{block.sub}</div>
			</div>
		</button>
	);
}

/**
 * Generatore report SLIMS: componi un report scegliendo ambito, periodo, blocchi
 * di contenuto e formato, con anteprima live — allineato a `#s-report` di
 * `SLIMS-CER_v11.html`, e lo storico dei report generati.
 */
const ReportBuilderView = ({
	cerNome,
	members,
	plants = [],
	aiEnabled = true,
	initialGenerated,
	membersLoading = false,
	view,
	onViewChange,
	configs = [],
	selectedConfigId = null,
	onSelectConfig,
	overviewGridSlot,
	onGenerateReport,
	generatedReports,
	onOpenReport,
	onDownloadReport,
	onDeleteReport,
	pdfPreview = null,
	onClosePdfPreview,
	onDownloadPdfPreview,
}: ReportBuilderViewProps) => {
	const [scope, setScope] = useState<ReportScope>('comunita');
	const [period, setPeriod] = useState('trimestre');
	const [format, setFormat] = useState<ReportFormat>('pdf');
	const [flags, setFlags] = useState({ logo: true, firma: true, grafici: false });
	const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>(() =>
		Object.fromEntries(PRESETS.comunita.map((id) => [id, true])),
	);
	const [socioId, setSocioId] = useState<string>('');
	const [plantId, setPlantId] = useState<string>('');
	const [customFrom, setCustomFrom] = useState(() => isoDay(firstOfMonth(new Date())));
	const [customTo, setCustomTo] = useState(() => isoDay(new Date()));
	const [generated, setGenerated] = useState<GeneratedReportItem[]>(initialGenerated);
	// Report in attesa di conferma di eliminazione (modale). Stato di solo display.
	const [pendingDelete, setPendingDelete] = useState<GeneratedReportItem | null>(null);

	const now = new Date();
	const blocks = aiEnabled ? BLOCKS : BLOCKS.filter((b) => !AI_BLOCK_IDS.has(b.id));
	// Senza una scelta vale il primo dell'elenco. Non si fissa all'apertura: i
	// soci arrivano dopo, e il socio restava vuoto — «Report Socio - -» coi
	// numeri di tutta la comunità (INB-395).
	const socio = members.find((m) => m.id === socioId) ?? members[0];
	const plant = plants.find((p) => p.id === plantId) ?? plants[0];
	const custom = { from: customFrom, to: customTo };
	const customValid = !!customFrom && !!customTo && customFrom <= customTo;

	// Il toggle Panoramica/Configurazione-singola esiste solo con più di una
	// configurazione in ambito: con una sola, la pagina è quella di prima.
	const configToggle =
		view != null && onViewChange != null && configs.length > 1 ? { view, onViewChange } : null;
	const selectedConfig = configs.find((c) => c.id === selectedConfigId) ?? null;
	const pageTitle =
		configToggle?.view === 'single' && selectedConfig ? selectedConfig.configNome : 'Report';

	function applyPreset(nextScope: ReportScope) {
		setScope(nextScope);
		setSelectedIds(Object.fromEntries(PRESETS[nextScope].map((id) => [id, true])));
	}

	function toggleBlock(id: string) {
		setSelectedIds((prev) => ({ ...prev, [id]: !prev[id] }));
	}

	function toggleCategory(cat: ReportBlockCategory) {
		const catBlocks = blocks.filter((b) => b.cat === cat);
		const allOn = catBlocks.every((b) => selectedIds[b.id]);
		setSelectedIds((prev) => {
			const next = { ...prev };
			for (const b of catBlocks) next[b.id] = !allOn;
			return next;
		});
	}

	const selectedBlocks = blocks.filter((b) => selectedIds[b.id]);
	const byCat = new Map<ReportBlockCategory, ReportBlockDef[]>();
	for (const b of selectedBlocks) byCat.set(b.cat, [...(byCat.get(b.cat) ?? []), b]);

	const docTitle =
		scope === 'socio'
			? `${SCOPE_TITLE.socio} - ${socio ? memberLabel(socio) : '-'}`
			: scope === 'impianto' && plant
				? `${SCOPE_TITLE.impianto} - ${plant.label}`
				: SCOPE_TITLE[scope];
	// Senza suffisso «S.C.»: la forma giuridica della CER non è nota qui, e
	// aggiungerla d'ufficio la dichiarava cooperativa.
	const docRef = scope === 'socio' ? (socio ? memberLabel(socio) : '-') : cerNome;
	const docMeta = `${docRef} · ${PERIOD_NAME[period] ?? period} · ${periodLabel(period, now, custom)}`;
	const pages = Math.max(1, Math.ceil(selectedBlocks.length / 3) + (flags.grafici ? 1 : 0));

	// Elenco controllato dal cablaggio (persistito) quando `generatedReports` è
	// passato; altrimenti lo stato interno alimentato da `initialGenerated`.
	const generatedList = generatedReports ?? generated;

	function handleGenerate() {
		if (selectedBlocks.length === 0) {
			toast.warning('Seleziona almeno un blocco');
			return;
		}
		if (scope === 'impianto' && !plant) {
			toast.warning('Nessun impianto da scegliere in questa configurazione');
			return;
		}
		if (period === 'custom' && !customValid) {
			toast.warning('Scegli le date del periodo: «Da» non può venire dopo «A»');
			return;
		}

		if (onGenerateReport) {
			onGenerateReport({
				scope,
				format,
				title: docTitle,
				meta: docMeta,
				subject: docRef,
				period,
				periodLabel: periodLabel(period, now, custom),
				flags,
				memberId: scope === 'socio' ? (socio?.id ?? null) : null,
				plantId: scope === 'impianto' ? (plant?.id ?? null) : null,
				customFrom: period === 'custom' ? customFrom : null,
				customTo: period === 'custom' ? customTo : null,
				sections: [...byCat.entries()].map(([cat, blocks]) => ({
					cat,
					label: CAT_META[cat].label,
					blocks: blocks.map((b) => ({
						id: b.id,
						title: b.title,
						sub: b.sub,
						value: null,
					})),
				})),
				pages,
				generatedAt: new Date().toISOString(),
			});
			return;
		}

		const today = now.toLocaleDateString('it-IT', {
			day: '2-digit',
			month: 'short',
			year: 'numeric',
		});
		setGenerated((prev) => [
			{
				id: `gen-${Date.now()}`,
				title: `${docTitle} - ${periodLabel(period, now, custom)}`,
				sub: `${format.toUpperCase()} · ${selectedBlocks.length} blocchi · ${today}`,
				tone: 'green',
				status: '✓ nuovo',
			},
			...prev,
		]);
		toast.success(`Report generato in formato ${format.toUpperCase()}`);
	}

	if (configToggle?.view === 'multi') {
		return (
			<div>
				<Row justify="between" wrap gap="md" className="mb-5">
					<div className="page-title flex items-center gap-2.5">Report</div>
					<Stack gap="sm" align="stretch">
						<SegmentedTabs
							items={REPORT_VIEW_TABS}
							value={configToggle.view}
							onChange={configToggle.onViewChange}
							ariaLabel="Vista dei report"
							className="mb-0"
						/>
					</Stack>
				</Row>
				{/* `@container` come nella Dashboard (`DashPanel`): senza, le container
				    query `@2xl:` di `DashGrid layout="configs"` non scattano mai e le
				    card restano incolonnate. */}
				<div className="@container">
					<Text as="div" variant="label" tone="muted" className="mb-2.5">
						Configurazioni selezionate · clicca una card per il generatore di report
					</Text>
					<DashGrid layout="configs">{overviewGridSlot}</DashGrid>
				</div>
			</div>
		);
	}

	return (
		<div>
			<Row justify="between" wrap gap="md" className="mb-5">
				<div className="page-title flex items-center gap-2.5">{pageTitle}</div>
				{configToggle && (
					<Stack gap="sm" align="stretch">
						<SegmentedTabs
							items={REPORT_VIEW_TABS}
							value={configToggle.view}
							onChange={configToggle.onViewChange}
							ariaLabel="Vista dei report"
							className="mb-0"
						/>
						{onSelectConfig && (
							<select
								value={selectedConfigId ?? ''}
								onChange={(e) => onSelectConfig(e.target.value)}
								aria-label="Configurazione"
								className="min-w-60 rounded-[7px] border border-border bg-card px-2.5 py-1.5 text-[13px] text-text-primary outline-none focus:border-accent"
							>
								{configs.map((c) => (
									<option key={c.id} value={c.id}>
										{c.cerNome} · {c.configNome}
									</option>
								))}
							</select>
						)}
					</Stack>
				)}
			</Row>

			{/* Builder + Preview */}
			<div className="rb-grid">
				{/* BUILDER */}
				<PanelCard title="Composizione report">
					<div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
						<div>
							<div className="rb-step-lbl">
								<span className="rb-step-n">1</span>
								Destinatario / ambito
							</div>
							<PillRow options={SCOPE_OPTIONS} value={scope} onChange={applyPreset} />
							{scope === 'socio' && (
								<div className="mt-2">
									{membersLoading ? (
										<div
											className="flex items-center gap-2"
											// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- guscio generico (`div`), stesso pattern di PanelCardSkeleton/DistributionBar.
											role="status"
											aria-label="Caricamento soci…"
										>
											<BodySkeleton className="h-3.5 w-10" />
											<BodySkeleton className="h-8 w-40 rounded-lg" />
										</div>
									) : members.length > 0 ? (
										<LabeledSelect label="Socio" value={socio?.id ?? ''} onChange={setSocioId}>
											{members.map((m) => (
												<option key={m.id} value={m.id}>
													{memberLabel(m)}
												</option>
											))}
										</LabeledSelect>
									) : (
										<p className="text-[13px] text-text-muted">
											Nessun socio disponibile - aggiungi soci nella sezione Soci.
										</p>
									)}
								</div>
							)}
							{scope === 'impianto' && (
								<div className="mt-2">
									{plants.length > 0 ? (
										<LabeledSelect label="Impianto" value={plant?.id ?? ''} onChange={setPlantId}>
											{plants.map((p) => (
												<option key={p.id} value={p.id}>
													{p.label}
												</option>
											))}
										</LabeledSelect>
									) : (
										<p className="text-[13px] text-text-muted">
											Nessun impianto in questa configurazione.
										</p>
									)}
								</div>
							)}
						</div>
						<div>
							<div className="rb-step-lbl">
								<span className="rb-step-n">2</span>
								Periodo
							</div>
							<PillRow options={PERIOD_OPTIONS} value={period} onChange={setPeriod} />
							{period === 'custom' && (
								<div className="mt-2 grid grid-cols-2 gap-2">
									<DetailField
										label="Da"
										error={customValid ? undefined : '«Da» non può venire dopo «A»'}
									>
										<DetailTextInput
											type="date"
											value={customFrom}
											onChange={setCustomFrom}
											max={isoDay(now)}
										/>
									</DetailField>
									<DetailField label="A">
										<DetailTextInput
											type="date"
											value={customTo}
											onChange={setCustomTo}
											max={isoDay(now)}
										/>
									</DetailField>
								</div>
							)}
						</div>
					</div>

					<div className="rb-step-lbl">
						<span className="rb-step-n">3</span>
						Blocchi di contenuto
					</div>
					<div id="rb-cats">
						{(Object.keys(CAT_META) as ReportBlockCategory[]).map((cat) => {
							const catBlocks = blocks.filter((b) => b.cat === cat);
							// Senza le raccomandazioni AI la categoria resta vuota: non si mostra.
							if (catBlocks.length === 0) return null;
							const meta = CAT_META[cat];
							return (
								<div key={cat}>
									<div className="rb-catrow">
										<span className="rb-cattitle">{meta.label}</span>
										<button
											type="button"
											className="rb-catall font-mono"
											onClick={() => toggleCategory(cat)}
										>
											seleziona tutti
										</button>
									</div>
									<div className="rb-blocks">
										{catBlocks.map((b) => (
											<BlockCheckbox
												key={b.id}
												block={b}
												checked={!!selectedIds[b.id]}
												onToggle={() => toggleBlock(b.id)}
											/>
										))}
									</div>
								</div>
							);
						})}
					</div>

					<div className="mt-[18px] grid grid-cols-1 gap-5 sm:grid-cols-2">
						<div>
							<div className="rb-step-lbl">
								<span className="rb-step-n">4</span>
								Formato
							</div>
							<PillRow options={FORMAT_OPTIONS} value={format} onChange={setFormat} />
						</div>
						<div>
							<div className="rb-step-lbl">
								<span className="rb-step-n">5</span>
								Opzioni
							</div>
							<FlagPillRow
								options={[
									{ key: 'logo', label: 'Logo' },
									{ key: 'firma', label: 'Spazio firma' },
									{ key: 'grafici', label: 'Grafici' },
								]}
								flags={flags}
								onToggle={(key) => setFlags((f) => ({ ...f, [key]: !f[key] }))}
							/>
						</div>
					</div>
				</PanelCard>

				{/* PREVIEW */}
				<div className="flex min-w-0 flex-col gap-5">
					<div className="rep-preview mb-1">
						<div className="flex items-center justify-between px-3.5 pt-3.5">
							<span className="card-title text-[13px]">Anteprima</span>
							<CardBadge tone="green">{format.toUpperCase()}</CardBadge>
						</div>
						<div className="rep-doc">
							<div className="rep-doc-hd">
								<div>
									<div className="rep-doc-title">{docTitle}</div>
									<div className="rep-doc-meta">{docMeta}</div>
								</div>
								{flags.logo && <div className="rep-doc-logo font-mono">SLIMS</div>}
							</div>
							{selectedBlocks.length === 0 ? (
								<div className="rep-empty">
									Nessun blocco selezionato.
									<br />
									Scegli i contenuti da includere nel report.
								</div>
							) : (
								[...byCat.entries()].map(([cat, blocks]) => (
									<div key={cat} className="rep-sec">
										<div className="rep-sec-h">{CAT_META[cat].label}</div>
										{blocks.map((b) => (
											<div key={b.id} className="rep-sec-l">
												• <b className="font-semibold text-text-primary">{b.title}</b> -{' '}
												<i>{REPORT_VALUE_UNAVAILABLE}</i>
											</div>
										))}
									</div>
								))
							)}
						</div>
						<div className="flex items-center justify-between px-3.5 pb-3.5 text-[11px] text-text-muted">
							<span>
								~{pages} pagine · {format.toUpperCase()}
							</span>
							<button type="button" onClick={handleGenerate} className="btn btn-green">
								Genera
							</button>
						</div>
					</div>

					<PanelCard title="Report generati">
						{generatedList.length === 0 ? (
							<p className="text-[13px] text-text-muted">Nessun report generato finora.</p>
						) : (
							generatedList.map((g) => (
								<ReportCardRow
									key={g.id}
									title={g.title}
									sub={g.sub}
									status={g.status}
									statusTone={g.tone}
									onOpen={onOpenReport ? () => onOpenReport(g.id) : undefined}
									onDownload={onDownloadReport ? () => onDownloadReport(g.id) : undefined}
									onDelete={onDeleteReport ? () => setPendingDelete(g) : undefined}
								/>
							))
						)}
					</PanelCard>
				</div>
			</div>

			{onDeleteReport && (
				<ConfirmationDialog
					open={pendingDelete != null}
					onOpenChange={(open) => {
						if (!open) setPendingDelete(null);
					}}
					onConfirm={() => {
						if (pendingDelete) onDeleteReport(pendingDelete.id);
					}}
					title="Eliminare il report?"
					description={
						pendingDelete
							? `«${pendingDelete.title}» verrà rimosso dallo storico. L'azione non è reversibile.`
							: ''
					}
					variant="destructive"
					confirmText="Elimina"
				/>
			)}

			<Dialog open={!!pdfPreview} onOpenChange={(open) => !open && onClosePdfPreview?.()}>
				<DialogContent
					hideCloseButton
					className="flex max-h-[90vh] w-[90vw] max-w-4xl flex-col gap-0 border-border bg-card p-0"
				>
					<DialogTitle className="sr-only">{pdfPreview?.title}</DialogTitle>
					<div className="flex items-center justify-between border-b border-border px-4 py-3">
						<div className="flex items-center gap-2">
							<FileText className="h-4 w-4 text-accent" />
							<span className="text-xs font-semibold">{pdfPreview?.title}</span>
						</div>
						<div className="flex items-center gap-2">
							<a
								href={pdfPreview?.url}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-1.5 rounded-[7px] border border-border bg-card px-3 py-1.5 text-[13px] text-text-muted transition-colors hover:border-accent hover:text-accent"
							>
								Apri in nuova scheda
							</a>
							{onDownloadPdfPreview && (
								<button
									type="button"
									onClick={onDownloadPdfPreview}
									className="inline-flex items-center gap-1.5 rounded-[7px] border border-border bg-card px-3 py-1.5 text-[13px] text-text-muted transition-colors hover:border-accent hover:text-accent"
								>
									<Download className="h-3.5 w-3.5" />
									Scarica
								</button>
							)}
							<button
								type="button"
								aria-label="Chiudi"
								onClick={() => onClosePdfPreview?.()}
								className="rounded p-1.5 text-text-muted transition-colors hover:bg-accent/10 hover:text-accent"
							>
								<X className="h-4 w-4" />
							</button>
						</div>
					</div>
					<div className="flex-1 overflow-hidden">
						{pdfPreview && (
							<iframe
								src={pdfPreview.url}
								className="h-[75vh] w-full border-0"
								title={pdfPreview.title}
							/>
						)}
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
};
ReportBuilderView.displayName = 'ReportBuilderView';

export { ReportBuilderView };
